import React, { useEffect, useContext, useRef, useState } from 'react';
import { string } from 'prop-types';

import {
  Accordion, AccordionHeader, AccordionBody,
  Typography
} from '@one-thd/sui-atomic-components';
import { useDataModel, QueryProvider } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { SAMCalculator, getCalculatorLocalStorage } from '@thd-olt-component-react/calculator';
import { Placeholder } from '@thd-olt-component-react/core-ui';
import { ScheduleAMeasureCta, GccCarpetSamCta } from '@thd-olt-component-react/home-services-cta-buttons';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';

import { MOBILE } from '@thd-olt-functional/utils';

import { dataModel } from './dataModel';
import { shouldDisplayScheduleAMeasure } from '../../utils/utils';
import { ServiceReview } from '../common/service-reviews/ServiceReview';
import { FIELDSET_TITLE, samHowToBuyCarpetSteps } from '../../utils/constants';

const NonBuyableScheduleAMeasure = ({ itemId, label }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('home-services.ready'); }, []);
  const { channel } = useContext(ExperienceContext);
  const { storeId } = useStore();
  const ctaButtonsRef = useRef();
  const hideGCCComponent = useConfigService('fs:hideGCCForInstalledCarpet');

  const opts = {
    skip: !itemId,
    variables: {
      itemId,
      storeId
    }
  };
  const { data } = useDataModel('product', opts);
  const { data: gccCheckData, loading: gccCheckLoading } = useDataModel('clientOnlyProduct', { ...opts, ssr: false });
  const { storageObj } = getCalculatorLocalStorage({ l1Label: label });

  const [openCalculator, setOpenCalculator] = useState(!!storageObj);

  useEffect(() => {
    setOpenCalculator(!!storageObj);
  }, [!!storageObj]);

  if (!itemId || !data || !shouldDisplayScheduleAMeasure(data)) {
    return null;
  }

  const handleChange = (event, isOpen) => setOpenCalculator(isOpen);

  const title = 'How to Buy Carpet';
  const target = '_self';
  const isMobile = channel === MOBILE;
  const isGccCarpet = gccCheckData?.product?.installServices?.gccCarpetDesignAndOrderEligible;

  const getCTAButtons = ({ isFooter = false, enableNewTab = false }) => {
    if (gccCheckLoading) {
      return <Placeholder height="76px" />;
    }
    const CTAElement = (isGccCarpet && !hideGCCComponent) ? GccCarpetSamCta : ScheduleAMeasureCta;
    return (
      <CTAElement
        itemId={itemId}
        target={target}
        isFooter={isFooter}
        enableNewTab={enableNewTab}
        hideContactInfo={isMobile && isFooter}
      />
    );
  };

  return (
    <div data-component="NonBuyableScheduleAMeasure">
      <Typography variant="body-xs" weight="bold" color="brand">
        {FIELDSET_TITLE}
      </Typography>
      <Typography variant="h3" weight="bold">
        {title}
      </Typography>
      <section className="sui-pb-2">
        <span className="sui-text-subtle sui-text-base sui-leading-tight">
          Free carpet samples when you schedule an in-home measure
        </span>
      </section>
      <ul className="sui-list-decimal sui-list-inside">
        {samHowToBuyCarpetSteps.map((item) => (
          <Accordion key={item.title}>
            <AccordionHeader>
              <Typography variant="body-lg" weight="bold" height="tight">
                <li>{item.title}</li>
              </Typography>
            </AccordionHeader>
            <AccordionBody>{item.content}</AccordionBody>
          </Accordion>
        ))}
      </ul>
      <div className="sui-pb-5 sui-pt-4" ref={ctaButtonsRef}>
        {getCTAButtons({})}
      </div>
      <ServiceReview serviceIdentifier="b76e9e302">
        <div>
          <QueryProvider cacheKey="service-reviews-sam">
            {getCTAButtons({ enableNewTab: true })}
          </QueryProvider>
        </div>
      </ServiceReview>
      { gccCheckLoading && (
        <Placeholder height="57px" />
      )}
      { (!isGccCarpet && !gccCheckLoading) && (
        <Accordion expanded={openCalculator} onChange={handleChange}>
          <AccordionHeader>How Much Flooring Will You Need?</AccordionHeader>
          <AccordionBody>
            <SAMCalculator itemId={itemId} />
          </AccordionBody>
        </Accordion>
      )}
      {isMobile && !gccCheckLoading && (
        <StickyWithHeaderObserver
          boxShadow
          className="sui-bg-primary sui-left-0 sui-py-2 sui-px-0 sui-w-full sui-hidden"
          forwardRef={ctaButtonsRef}
          position="bottom"
          stickyByDefault
          stickyType="forwardRef"
          visibleByDefault
        >
          {getCTAButtons({ isFooter: true })}
        </StickyWithHeaderObserver>
      )}
    </div>
  );
};

NonBuyableScheduleAMeasure.displayName = 'NonBuyableScheduleAMeasure';

NonBuyableScheduleAMeasure.propTypes = {
  itemId: string.isRequired,
  label: string
};

NonBuyableScheduleAMeasure.defaultProps = {
  label: 'Flooring'
};

NonBuyableScheduleAMeasure.dataModel = dataModel;

export { NonBuyableScheduleAMeasure };